import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import type { InstantSearch } from 'react-instantsearch';

import type {
  extractQueryGeneric,
  extractVariableGeneric,
} from '#src/lib/searchClient.ts';

import type {
  DefaultPrintablePlayerFacetValuesSearchQuery,
  DefaultPrintablePlayerFacetValuesSearchQueryVariables,
  useDefaultPrintablePlayersSearchClient_searchPrintablePlayers,
} from './__generated__/useDefaultPrintablePlayersSearchClient.graphql.ts';
import type {
  BaseGenericQuery,
  BaseQueryVariables,
  MustContainSearchPlayers,
} from './usePrintablePlayersSearchClient.ts';
import { usePrintablePlayersSearchClient } from './usePrintablePlayersSearchClient.ts';

type SearchClient = ComponentProps<typeof InstantSearch>['searchClient'];

export const DEFAULT_PRINTABLE_PLAYER_FACET_VALUES_SEARCH_QUERY = gql`
  query DefaultPrintablePlayerFacetValuesSearchQuery(
    $field: String!
    $facetQuery: String!
    $query: String!
    $limit: Int
    $advancedFilters: String
    $refinements: [SearchRefinementInput!]!
    $onlyFollowed: Boolean
  ) {
    searchPlayerFacetValues(
      field: $field
      facetQuery: $facetQuery
      query: $query
      limit: $limit
      advancedFilters: $advancedFilters
      refinements: $refinements
      onlyFollowed: $onlyFollowed
    ) {
      ...usePrintablePlayersSearchClient_searchFacetValue
    }
  }
  ${usePrintablePlayersSearchClient.fragments.searchFacetValue}
` as TypedDocumentNode<
  DefaultPrintablePlayerFacetValuesSearchQuery,
  DefaultPrintablePlayerFacetValuesSearchQueryVariables
>;

type DefaultFacetQuery =
  typeof DEFAULT_PRINTABLE_PLAYER_FACET_VALUES_SEARCH_QUERY;

export const useDefaultPrintablePlayersSearchClient = <
  TQuery extends BaseGenericQuery<MustContainSearchPlayers<object>, TVariables>,
  TVariables extends BaseQueryVariables = extractVariableGeneric<TQuery>,
>(
  query: TQuery,
  options: {
    needsComposeTeamBench?: boolean;
    onlyFollowed: boolean;
    fetchPolicy?: 'network-only' | 'cache-first';
    variables?: Record<string, unknown>;
    skip?: boolean;
  },
  extractSearchPlayers: (
    data: extractQueryGeneric<MustContainSearchPlayers<TQuery>>
  ) => useDefaultPrintablePlayersSearchClient_searchPrintablePlayers | undefined
): SearchClient => {
  const searchClient = usePrintablePlayersSearchClient<
    TQuery,
    DefaultFacetQuery
  >(query, options, extractSearchPlayers, {
    fQuery: DEFAULT_PRINTABLE_PLAYER_FACET_VALUES_SEARCH_QUERY,
    extractSearchPlayersFacetValues: (
      data: extractQueryGeneric<DefaultFacetQuery>
    ) => data.searchPlayerFacetValues,
  });

  return searchClient;
};

useDefaultPrintablePlayersSearchClient.fragments = {
  searchPrintablePlayers: gql`
    fragment useDefaultPrintablePlayersSearchClient_searchPrintablePlayers on SearchPlayers {
      ...usePrintablePlayersSearchClient_searchPrintablePlayers
    }
    ${usePrintablePlayersSearchClient.fragments.searchPrintablePlayers}
  ` as TypedDocumentNode<useDefaultPrintablePlayersSearchClient_searchPrintablePlayers>,
};
